// src/features/satisfaction/components/SurveyResponsesCard.tsx
//
// The «Respuestas» list of the Satisfacción page: one row per journey, newest
// first, server-paginated, with a «Solo con comentario» switch. The comment is
// text written by the guest: rendered as plain text (React escapes it), never
// logged, never sent anywhere else.
import React from 'react';
import { Box, FormControlLabel, Switch, TablePagination, Typography } from '@mui/material';
import { QuestionAnswerRounded as ResponsesIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { WidgetCard } from '@/components/common/WidgetCard';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import {
  formatSurveyAnsweredAt,
  formatSurveyScore,
  surveyChannelLabel,
  surveyLanguageLabel,
} from '@/lib/surveyView';
import type { SurveyResponseRow, SurveyResponsesPage } from '@/types/analyticsV2';

export const SURVEY_PAGE_SIZES = [25, 50, 100];

interface SurveyResponsesCardProps {
  data: SurveyResponsesPage | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** 1-based, as the API counts. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  withComment: boolean;
  onWithCommentChange: (value: boolean) => void;
  /** The property's IANA time zone: answer times are shown in it. */
  timeZone?: string;
  locale: string;
}

const Text: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="span">
    {children}
  </Typography>
);

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
    {children}
  </Typography>
);

export const SurveyResponsesCard: React.FC<SurveyResponsesCardProps> = ({
  data,
  loading,
  error,
  onRetry,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  withComment,
  onWithCommentChange,
  timeZone,
  locale,
}) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.satisfaction.list.${key}`);

  const score = (value: number | null) => {
    const text = formatSurveyScore(value);
    return text === null ? <Muted>{t('notAnswered')}</Muted> : <Text>{text}</Text>;
  };
  const language = (code: string | null) => {
    const text = surveyLanguageLabel(code, locale);
    return text === null ? <Muted>{t('notAnswered')}</Muted> : <Text>{text}</Text>;
  };

  const columns: MetricsColumn<SurveyResponseRow>[] = [
    {
      key: 'answeredAt',
      label: t('answeredAt'),
      render: (row) => (
        <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap', fontWeight: 'inherit' }}>
          {formatSurveyAnsweredAt(row.answeredAt, timeZone, locale)}
        </Typography>
      ),
    },
    { key: 'channel', label: t('channel'), render: (row) => <Text>{surveyChannelLabel(row.channel, translate)}</Text> },
    { key: 'language', label: t('language'), render: (row) => language(row.language) },
    { key: 'overall', label: t('overall'), align: 'right', render: (row) => score(row.overall) },
    { key: 'ease', label: t('ease'), align: 'right', render: (row) => score(row.ease) },
    {
      key: 'comment',
      label: t('comment'),
      block: true,
      render: (row) =>
        row.comment ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', minWidth: { sm: 220 } }}>
            {row.comment}
          </Typography>
        ) : (
          <Muted>{t('noComment')}</Muted>
        ),
    },
  ];

  const rows = data?.items ?? [];
  const showEmpty = !loading && !error && data !== null && rows.length === 0;

  return (
    <WidgetCard
      title={t('title')}
      description={t('timeZone')}
      icon={<ResponsesIcon />}
      action={
        <FormControlLabel
          sx={{ mr: 0 }}
          control={<Switch size="small" checked={withComment} onChange={(e) => onWithCommentChange(e.target.checked)} />}
          label={<Typography variant="body2">{t('onlyWithComment')}</Typography>}
        />
      }
    >
      {error ? (
        <FetchErrorBanner onRetry={onRetry} />
      ) : showEmpty ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          {withComment ? t('emptyWithComment') : t('empty')}
        </Typography>
      ) : (
        <>
          <ResponsiveMetricsTable columns={columns} rows={rows} getRowKey={(row) => row.journeyId} loading={loading} skeletonRowCount={5} />
          <TablePagination
            component="div"
            count={data?.totalItems ?? 0}
            page={Math.max(0, page - 1)}
            onPageChange={(_, next) => onPageChange(next + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={SURVEY_PAGE_SIZES}
            labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              mt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              // Phone width: the controls wrap instead of pushing «next page» out of the card.
              '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'flex-end', px: { xs: 0, sm: 2 } },
              '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
            }}
          />
        </>
      )}
    </WidgetCard>
  );
};

export default SurveyResponsesCard;
